import { ActivityRegistry, ActivityType, ScheduleType } from 'types/models';
import {
    Box,
    Checkbox,
    Flex,
    IconButton,
    Tooltip,
    UseToastOptions,
    useToast
} from '@chakra-ui/react';
import {
    CustomTable,
    CustomTableButtonWrapper,
    CustomTd,
    DashboardPageContent,
    FormWrapper,
    PageSubtitle,
    PageTitle,
} from 'custom-style-components';
import { FormHandles, SubmitHandler } from '@unform/core';
import {
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
} from '@chakra-ui/modal';
import React, { useEffect, useRef, useState } from 'react';
import Select, { SelectOption } from 'components/Select';
import {
    downloadPresenceListPdf,
    downloadRegistryPdf,
    downloadRegistryXls
} from 'utils/reportUtils';
import {
    getDate,
    renderDateAsDayMonth,
    renderDateAsTime
} from 'utils/dateUtils';
import { useParams, useRouteMatch } from 'react-router-dom';

import COLORS from 'constants/COLORS';
import CustomButton from 'components/Button';
import { DeleteIcon } from '@chakra-ui/icons';
import ErrorMessage from 'components/ErrorMessage';
import { Form } from '@unform/web';
import { GenericFrontError } from 'errors/GenericFrontError';
import GoBackButton from 'components/GoBackButton';
import LoadingSpinner from 'components/LoadingSpinner';
import { ResponseError } from 'errors/ResponseError';
import TOAST from 'constants/TOAST';
import { api } from 'services/axios';
import createCancelTokenSource from 'utils/createCancelTokenSource';
import { fetchManyRegistriesByActivity } from 'services/fetch/registries';
import { fetchManyUsers } from 'services/fetch/users';
import { fetchOneActivity } from 'services/fetch/activities';
import { maskCpf } from 'utils/maskCpf';
import { stringifyUserWithCpf } from 'utils/stringifyUserWithCpf';
import { useAuth } from 'hooks/auth';

interface ParamTypes {
    activityId: string;
}

const MarkPresences: React.FC = () => {
    const toastId = 'presence';
    const toast = useToast({
        id: toastId,
        duration: TOAST.DURATION,
        position: TOAST.DEFAULT_POSITION,
        isClosable: TOAST.CLOSABLE_BY_DEFAULT,
    });

    const toaster = (options: UseToastOptions) => {
        if (toast.isActive(toastId)) {
            toast.close(toastId);

            setTimeout(() => toast(options), 150);
        } else {
            toast(options);
        }
    };

    const { activityId } = useParams<ParamTypes>();
    const { user } = useAuth();
    const { path } = useRouteMatch();
    const isResponsibleWindow = path.includes('responsavel');
    const isAdminWindow = path.includes('admin') && (path.includes('presencas') || path.includes('inscritos'));
    const isOrganizerWindow =
        path.includes('organizador') && (path.includes('presencas') || path.includes('inscritos'));

    const [submittedPresence, setSubmittedPresence] = useState({
        loading: false,
        user: -1,
        schedule: -1,
    });
    const [isSubmiting, setIsSubmiting] = useState(false);

    const [readyForEmission, setReadyForEmission] = useState<boolean>();
    const [registryCompleted, setRegistryCompleted] = useState<boolean>();

    const [schedules, setSchedules] = useState<ScheduleType[]>();

    const [activity, setActivity] = useState<ActivityType>();
    const [registryList, setRegistryList] = useState<ActivityRegistry[]>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [conflictModalOpen, setConflictModalOpen] = useState(false);
    const [conflictData, setConflictData] = useState<{
        userId: number;
        message: string;
    } | null>(null);

    const formRef = useRef<FormHandles>(null);
    const [usersList, setUsersList] = useState<SelectOption[]>([]);
    const [submitIsLoading, setSubmitIsLoading] = useState(false);

    useEffect(() => {
        const source = createCancelTokenSource();
        setLoading(true);
        if (user) {
            fetchOneActivity(source.token, activityId)
                .then((activity) => {
                    setActivity(activity);
                    const scheduleList = activity.schedules;

                    const currentDate = getDate();
                    const isReady = scheduleList.every(
                        (schedule) => schedule.startDate < currentDate
                    );
                    setRegistryCompleted(activity.readyForCertificateEmission);
                    setReadyForEmission(isReady);
                    setSchedules(activity.schedules);
                    return fetchManyRegistriesByActivity(
                        source.token,
                        activityId,
                        {
                            limit: Number.MAX_SAFE_INTEGER,
                        }
                    );
                })
                .then(({ registries }) => {
                    setRegistryList(registries);
                    return fetchManyUsers(source.token, {
                        limit: Number.MAX_SAFE_INTEGER,
                    });
                })
                .then(({ users }) => {
                    setUsersList(
                        users.map((u) => ({
                            value: u.id,
                            label: stringifyUserWithCpf(u),
                        }))
                    );
                })
                .catch((err) => {
                    setError(err.message);
                    toast({
                        title: err.message,
                        status: 'error',
                    });
                })
                .finally(() => setLoading(false));
        }
        return () => source.cancel();
    }, []);

    const onConfirmConflict = async () => {
        if (!conflictData) return;
        setSubmitIsLoading(true);
        setConflictModalOpen(false);
        api.post(`/activity/registry/add/${activityId}/${conflictData.userId}?force=true`)
            .then(() => {
                toaster({
                    title: 'Inscrito com sucesso!',
                    status: 'success',
                });
                refreshRegistries();
            })
            .catch((err) => {
                toaster({
                    title: 'Erro inesperado ao forçar inscrição',
                    status: 'error',
                });
            })
            .finally(() => {
                setSubmitIsLoading(false);
                setConflictData(null);
            });
    };


    const refreshRegistries = () => {
        const source = createCancelTokenSource();
        fetchManyRegistriesByActivity(source.token, activityId, {
            limit: Number.MAX_SAFE_INTEGER,
        })
            .then(({ registries }) => {
                setRegistryList(registries);
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const onSubmit: SubmitHandler<{ users: number }> = async (data) => {
        setSubmitIsLoading(true);
        api.post(`/activity/registry/add/${activityId}/${data.users}`)
            .then(() => {
                toaster({
                    title: 'Inscrito!',
                    status: 'success',
                });
                refreshRegistries();
            })
            .catch((err) => {
                let message = 'Erro inesperado';
                if (err instanceof GenericFrontError) {
                    message = err.message;
                    if (err instanceof ResponseError && err.status === 409) {
                        const activityName = err.response?.data?.[0]?.activityName;
                        setConflictData({
                            userId: data.users,
                            message: `O participante já está inscrito na atividade "${activityName}" que ocorre no mesmo horário desta. Deseja removê-lo de lá e adicioná-lo nesta nova?`
                        });
                        setConflictModalOpen(true);
                        return;
                    } else if (
                        err instanceof ResponseError &&
                        err.status === 400
                    ) {
                        message =
                            'Este usuário já está inscrito nesta atividade';
                    } else if (
                        err instanceof ResponseError &&
                        err.status === 403
                    ) {
                        message =
                            'Selecione um usuário para realizar a inscrição';
                    } else if (
                        err instanceof ResponseError &&
                        err.status === 401
                    ) {
                        message =
                            'O evento está invisível, portanto não é possível realizar inscrições';
                    }
                }
                toaster({
                    title: message,
                    status: 'error',
                });
            })
            .finally(() => setSubmitIsLoading(false));
    };

    const renderSchedules = () => {
        return schedules?.map((schedule, index) => {
            return (
                <th key={index + 100}>
                    {renderDateAsDayMonth(schedule.startDate)},{' '}
                    {renderDateAsTime(schedule.startDate)}
                </th>
            );
        });
    };

    const handlePresenceEmission = (
        toCheck: boolean,
        scheduleId: number,
        userId: number
    ) => {
        const url = `/activity/presence/schedule/${scheduleId}/user/${userId}`;
        setSubmittedPresence({
            loading: true,
            schedule: scheduleId,
            user: userId,
        });

        const apiCall = toCheck ? api.post : api.delete;

        apiCall(url)
            .then(() =>
                toast({
                    title: toCheck ? 'Presença marcada' : 'Presença desmarcada',
                    duration: 1500,
                    status: 'info',
                })
            )
            .catch((err) => {
                console.log(err);
                toast({
                    title: err.message,
                    status: 'error',
                });
            })
            .finally(() =>
                setSubmittedPresence({
                    loading: false,
                    schedule: -1,
                    user: -1,
                })
            );
    };

    const handleActivityPresenceEmission = () => {
        setIsSubmiting(true);
        api.put(`/activity/${activityId}`, {
            readyForCertificateEmission: true,
        })
            .then(() => {
                setRegistryCompleted(true);
                toast({
                    title: 'As presenças foram emitidas',
                    status: 'success',
                });
            })
            .catch((err) => {
                console.log(err);
                toast({
                    title: err.message,
                    status: 'error',
                });
            })
            .finally(() => {
                setIsSubmiting(false);
                setConfirmModalOpen(false);
            });
    };

    const handleActivityPresenceSupress = () => {
        setIsSubmiting(false);
        api.put(`/activity/${activityId}`, {
            readyForCertificateEmission: false,
        })
            .then(() => {
                setRegistryCompleted(false);
                toast({
                    title: 'As presenças podem ser alteradas',
                    status: 'info',
                });
            })
            .catch((err) => {
                console.log(err);
                toast({
                    title: err.message,
                    status: 'error',
                });
            });
    };

    const unregisterUser = (userId: number) => {
        if (user) {
            api.delete(`/activity/registry/${activity?.id}/${userId}`)
                .then(() => {
                    toaster({
                        title: 'Inscrição removida',
                        status: 'success',
                    });
                    refreshRegistries();
                })
                .catch((err) => {
                    let message = 'Erro inesperado';
                    if (err instanceof GenericFrontError) {
                        message = err.message;
                    }
                    toaster({
                        title: message,
                        status: 'error',
                    });
                });
        }
    };

    const renderIfNoParticipant = () => {
        let message = 'Não há participantes nessa atividade';
        if (readyForEmission)
            message += ' Você pode optar por finalizar a atividade';
        return <div>{message}</div>;
    };

    const renderTableElements = (registries: ActivityRegistry[]) => {
        return registries.map((registry, index) => {
            return (
                <tr key={index}>
                    <CustomTd>
                        <CustomTableButtonWrapper>
                            <Tooltip
                                hasArrow
                                fontSize="1.2rem"
                                label="Remover Inscrição"
                            >
                                <IconButton
                                    variant="ghost"
                                    color="red.600"
                                    fontSize="1.5rem"
                                    icon={<DeleteIcon />}
                                    aria-label="Remover Inscrição"
                                    onClick={() =>
                                        unregisterUser(registry.user.id)
                                    }
                                />
                            </Tooltip>
                        </CustomTableButtonWrapper>
                    </CustomTd>
                    <CustomTd>
                        <div>{registry.user.name}</div>
                        <div>CPF {maskCpf(registry.user.cpf || '')}</div>
                        <div>
                            Data de inscrição:{' '}
                            {renderDateAsDayMonth(registry.registryDate) +
                                ' ' +
                                renderDateAsTime(registry.registryDate)}
                        </div>
                    </CustomTd>
                    {registry.presences.map((presence, index) => {
                        const targetSchedule =
                            registry.activity.schedules[index];
                        const canEnable = targetSchedule.startDate < getDate();
                        return (
                            <CustomTd align="center" key={index}>
                                {canEnable ? (
                                    <Checkbox
                                        onChange={(e) => {
                                            handlePresenceEmission(
                                                e.target.checked,
                                                registry.activity.schedules[
                                                    index
                                                ].id,
                                                registry.user.id
                                            );
                                        }}
                                        isDisabled={
                                            registryCompleted || isSubmiting
                                        }
                                        defaultChecked={presence.isPresent}
                                        colorScheme="green"
                                        size="lg"
                                    >
                                        {submittedPresence.loading &&
                                            submittedPresence.user ===
                                            registry.user.id &&
                                            submittedPresence.schedule ===
                                            targetSchedule.id ? (<LoadingSpinner />) :
                                            (
                                                <>
                                                    <p
                                                        style={{
                                                            fontSize: '1.6rem',
                                                        }}
                                                    >
                                                        Presente
                                                    </p>
                                                </>
                                            )}
                                    </Checkbox>
                                ) : (
                                    'Para ocorrer'
                                )}
                            </CustomTd>
                        );
                    })}
                </tr>
            );
        });
    };

    return (
        <DashboardPageContent>
            <Modal
                isOpen={conflictModalOpen}
                onClose={() => setConflictModalOpen(false)}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Conflito de Horário</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {conflictData?.message}
                    </ModalBody>
                    <ModalFooter>
                        <CustomButton
                            disabled={submitIsLoading}
                            style={{
                                backgroundColor: COLORS.success,
                                marginRight: '0.8rem',
                            }}
                            onClick={() => onConfirmConflict()}
                        >
                            Sim
                        </CustomButton>
                        <CustomButton
                            disabled={submitIsLoading}
                            style={{ backgroundColor: COLORS.danger }}
                            onClick={() => setConflictModalOpen(false)}
                        >
                            Não
                        </CustomButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <Modal
                isOpen={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
            >
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Deseja concluir a presença?</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        Ao concluir a presença, sua atividade ficará pronta para
                        os certificados. Até o fim do evento ainda será possivel
                        alterar as presenças.
                    </ModalBody>
                    <ModalFooter>
                        <CustomButton
                            style={{
                                backgroundColor: COLORS.success,
                                marginRight: '0.8rem',
                            }}
                            onClick={() => handleActivityPresenceEmission()}
                        >
                            Emitir
                        </CustomButton>
                        <CustomButton
                            style={{ backgroundColor: COLORS.danger }}
                            onClick={() => setConfirmModalOpen(false)}
                        >
                            Cancelar
                        </CustomButton>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            <FormWrapper>
                <PageTitle>Controle de presenças</PageTitle>
                <PageSubtitle>{activity && activity.title}</PageSubtitle>
                <GoBackButton />

                {!loading && activity && (
                    <Box bg="gray.50" p={6} borderRadius="md" mb={6} mt={4} borderWidth="1px" borderColor="gray.200">
                        <Form onSubmit={onSubmit} ref={formRef}>
                            <Flex align="flex-end" gap={4}>
                                <Box flex="1">
                                    <Select
                                        name="users"
                                        label="Adicionar Participante"
                                        options={usersList}
                                        placeholder="Escolha o participante"
                                    />
                                </Box>
                                <CustomButton
                                    disabled={submitIsLoading}
                                    style={{ backgroundColor: COLORS.success, height: '42px', marginBottom: '0.5rem', whiteSpace: 'nowrap', padding: '0 1.5rem' }}
                                >
                                    Adicionar
                                </CustomButton>
                            </Flex>
                        </Form>
                    </Box>
                )}

                {error ? (
                    <ErrorMessage>{error}</ErrorMessage>
                ) : loading ? (
                    <LoadingSpinner />
                ) : registryList && registryList.length > 0 ? (
                    <CustomTable>
                        <thead>
                            <tr>
                                <th>Ações</th>
                                <th>Participante</th>
                                {renderSchedules()}
                            </tr>
                        </thead>

                        <tbody>
                            {registryList && renderTableElements(registryList)}
                        </tbody>
                    </CustomTable>
                ) : (
                    renderIfNoParticipant()
                )}
                {!loading && (
                    <div style={{ marginTop: '0.8rem' }}>
                        {!registryCompleted &&
                            activity &&
                            registryList &&
                            (isResponsibleWindow ||
                                isAdminWindow ||
                                isOrganizerWindow) &&
                            (<>
                                <CustomButton
                                    style={{
                                        backgroundColor: COLORS.success,
                                        marginRight: '0.8rem',
                                    }}
                                    title={
                                        !readyForEmission
                                            ? 'Não é possível emitir presenças quando todas as datas da atividade não ocorreram'
                                            : undefined
                                    }
                                    noLoading={!readyForEmission}
                                    disabled={
                                        !readyForEmission || isSubmiting
                                    }
                                    onClick={() =>
                                        setConfirmModalOpen(true)
                                    }>
                                    Concluir atividade
                                </CustomButton>
                                <CustomButton
                                    style={{
                                        backgroundColor:
                                            COLORS.primaryLight,
                                        marginRight: '0.8rem',
                                    }}
                                    noLoading={!readyForEmission}
                                    disabled={
                                        !readyForEmission || isSubmiting
                                    }
                                    onClick={() =>
                                        downloadPresenceListPdf(
                                            activity,
                                            registryList
                                        )
                                    }
                                >
                                    Baixar Lista de Presença
                                </CustomButton>
                            </>
                            )}
                        {registryCompleted &&
                            (isResponsibleWindow ||
                                isAdminWindow ||
                                isOrganizerWindow) &&
                            (<CustomButton
                                style={{
                                    backgroundColor: COLORS.success,
                                    marginRight: '0.8rem',
                                }}
                                title={
                                    !readyForEmission
                                        ? 'Não é possível emitir presenças quando todas as datas da atividade não ocorreram'
                                        : undefined
                                }
                                noLoading={!readyForEmission}
                                onClick={() =>
                                    handleActivityPresenceSupress()
                                }
                            >
                                Alterar presenças
                            </CustomButton>
                            )}
                        {registryCompleted && activity && registryList && (
                            <>
                                <CustomButton
                                    style={{
                                        backgroundColor: COLORS.primaryLight,
                                        marginRight: '0.8rem',
                                    }}
                                    onClick={() =>
                                        downloadRegistryPdf(
                                            activity,
                                            registryList
                                        )
                                    }
                                >
                                    Baixar Relatório
                                </CustomButton>
                                <CustomButton
                                    style={{
                                        backgroundColor: COLORS.primaryLight,
                                        marginRight: '0.8rem',
                                    }}
                                    onClick={() =>
                                        downloadRegistryXls(
                                            activity,
                                            registryList,
                                            'xlsx'
                                        )
                                    }
                                >
                                    Gerar presença .XLSX
                                </CustomButton>
                                <CustomButton
                                    style={{
                                        backgroundColor: COLORS.primaryLight,
                                        marginRight: '0.8rem',
                                    }}
                                    onClick={() =>
                                        downloadRegistryXls(
                                            activity,
                                            registryList,
                                            'xls'
                                        )
                                    }
                                >
                                    Gerar presença .XLS
                                </CustomButton>
                            </>
                        )}
                    </div>
                )}
            </FormWrapper>
        </DashboardPageContent>
    );
};

export default MarkPresences;
